import React, { useState, useEffect, useRef } from 'react';
import {
  Card, Button, Space, Typography, Row, Col, Input, Form,
  Tag, Divider, Modal, Select, message, Image
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, FileImageOutlined,
  VideoCameraOutlined, FileOutlined, LinkOutlined, AppstoreOutlined,
  MessageOutlined
} from '@ant-design/icons';
import MediaUpload from './MediaUpload';
import CustomImageSelector from './CustomImageSelector';
import MaterialSelector from '../MaterialSelector';
import MaterialUploadModal from '../MaterialUploadModal';
import api from '../../services/api';
import './AttachmentManager.css'; // 添加样式文件

const { Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// 计算字符串的字节长度（UTF-8编码）
const getByteLength = (str) => {
  if (!str) return 0;
  return new Blob([str]).size;
};

const AttachmentManager = ({
  attachments = [], // inputBoxes 或 momentBoxes
  onUpdateContent,
  onUpdateType,
  onAddAttachment,
  onRemoveAttachment,
  mediaState,
  previewUrls,
  uploading,
  uploadProgress,
  handleFileUpload,
  handleRemoveFile,
  isTemplateActive = false,
  stepType = 'MESSAGE', // MESSAGE 或 MOMENTS
  validateUrl,
  handleUpdatePreviewUrls, // 新增：直接更新全局预览URLs的函数
  onValidationStateChange, // 新增：验证状态变化回调
  maxNonTextAttachments = 9, // 新增：非文字附件上限，企微群欢迎语为1
  materialSelectorMultiSelect // 新增：可覆写素材选择器是否多选
}) => {

  // 素材选择器状态
  const [materialSelectorVisible, setMaterialSelectorVisible] = useState(false);
  const [currentAttachmentId, setCurrentAttachmentId] = useState(null);
  const [materialType, setMaterialType] = useState(null);

  // 封面素材选择器状态（新增）
  const [coverMaterialSelectorVisible, setCoverMaterialSelectorVisible] = useState(false);
  const [editingAttachmentForCover, setEditingAttachmentForCover] = useState(null);

  // 素材上传Modal状态
  const [materialUploadVisible, setMaterialUploadVisible] = useState(false);
  const [uploadMaterialType, setUploadMaterialType] = useState('TEXT');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // 使用ref跟踪最新的attachments，解决闭包陷阱问题
  const attachmentsRef = useRef(attachments);
  
  // 保持ref与props同步
  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);

  // 获取租户ID
  const getTenantId = () => {
    try {
      const tenantStr = localStorage.getItem('tenant');
      if (tenantStr) {
        const tenant = JSON.parse(tenantStr);
        return tenant.id;
      }
    } catch (error) {
      console.error('获取租户ID失败:', error);
    }
    return null;
  };

  // 定义不同步骤类型支持的附件类型
  const getAttachmentTypes = () => {
    if (stepType === 'MESSAGE') {
      const types = [
        { key: 'IMAGE_GROUP', label: '图片', icon: <FileImageOutlined />, color: 'green' },
        { key: 'VIDEO', label: '视频', icon: <VideoCameraOutlined />, color: 'purple' },
        { key: 'LINK', label: '链接', icon: <LinkOutlined />, color: 'cyan' },
        { key: 'MINIPROGRAM', label: '小程序', icon: <AppstoreOutlined />, color: 'magenta' }
      ];
      
      // 检查是否已经有图片组，如果有则禁用图片选项
      const existingImageGroup = attachments.find(att => att.type === 'IMAGE_GROUP');
      if (existingImageGroup) {
        const imageType = types.find(t => t.key === 'IMAGE_GROUP');
        if (imageType) {
          imageType.disabled = true;
          imageType.disabledReason = '已有图片组';
        }
      }
      
      return types;
    } else if (stepType === 'MOMENTS') {
      const types = [
        { key: 'MOMENT_IMAGE_GROUP', label: '图片', icon: <FileImageOutlined />, color: 'green' },
        { key: 'MOMENT_VIDEO', label: '视频', icon: <VideoCameraOutlined />, color: 'purple' },
        { key: 'MOMENT_LINK', label: '链接', icon: <LinkOutlined />, color: 'cyan' }
        // 注意：朋友圈不支持小程序类型
        // { key: 'MINIPROGRAM', label: '小程序', icon: <AppstoreOutlined />, color: 'magenta', disabled: true, disabledReason: '朋友圈不支持小程序' }
      ];
      
      // 检查当前已有的媒体附件类型，用于禁用互斥选项
      const mediaAttachmentTypes = ['MOMENT_IMAGE_GROUP', 'MOMENT_VIDEO', 'MOMENT_LINK'];
      const currentMediaAttachments = attachments.filter(att => 
        mediaAttachmentTypes.includes(att.type)
      );
      
      // 如果已经有媒体附件，标记其他类型为禁用
      if (currentMediaAttachments.length > 0) {
        const existingType = currentMediaAttachments[0].type;
        types.forEach(type => {
          if (type.key !== existingType) {
            type.disabled = true;
            type.disabledReason = '朋友圈中图片、视频、链接只能三选一';
          } else if (type.key === existingType) {
            // 同类型也禁用，因为朋友圈中每种类型只能有一个
            type.disabled = true;
            type.disabledReason = `已有${type.label}`;
          }
        });
      }
      
      return types;
    }
    return [];
  };

  const attachmentTypes = getAttachmentTypes();
  const attachmentLimit = typeof maxNonTextAttachments === 'number' ? maxNonTextAttachments : 9;


  // 处理添加附件
  const handleAddAttachment = (type) => {
    // 计算当前附件总数（图片集中的每张图片都算一个附件）
    const currentCount = getTotalAttachmentCount();
    
    // 检查是否会超过限制
    if (currentCount >= attachmentLimit) {
      Modal.warning({
        title: '附件数量限制',
        content: `最多只能添加${attachmentLimit}个附件（图片集中每张图片算作1个附件）`,
      });
      return;
    }

    // 图片组限制：每种类型的图片组只能有一个
    if (type === 'IMAGE_GROUP' || type === 'MOMENT_IMAGE_GROUP') {
      const existingImageGroup = attachments.find(att => att.type === type);
      if (existingImageGroup) {
        const typeName = type === 'IMAGE_GROUP' ? '图片' : '朋友圈图片';
        Modal.warning({
          title: '图片组限制',
          content: `只能添加一个${typeName}组，请使用现有图片组上传多张图片。`,
        });
        return;
      }
    }

    // 朋友圈类型的特殊检查：图片、视频、链接、小程序只能四选一
    if (stepType === 'MOMENTS') {
      const mediaAttachmentTypes = ['MOMENT_IMAGE_GROUP', 'MOMENT_VIDEO', 'MOMENT_LINK', 'MINIPROGRAM'];
      const currentMediaAttachments = attachments.filter(att => 
        mediaAttachmentTypes.includes(att.type)
      );
      
      // 如果要添加的是媒体附件类型
      if (mediaAttachmentTypes.includes(type)) {
        // 检查是否已经有其他类型的媒体附件
        const existingMediaType = currentMediaAttachments.find(att => att.type !== type);
        if (existingMediaType) {
          let existingTypeName = '';
          let newTypeName = '';
          
          if (existingMediaType.type === 'MOMENT_IMAGE_GROUP') existingTypeName = '图片';
          else if (existingMediaType.type === 'MOMENT_VIDEO') existingTypeName = '视频';
          else if (existingMediaType.type === 'MOMENT_LINK') existingTypeName = '链接';
          else if (existingMediaType.type === 'MINIPROGRAM') existingTypeName = '小程序';
          
          if (type === 'MOMENT_IMAGE_GROUP') newTypeName = '图片';
          else if (type === 'MOMENT_VIDEO') newTypeName = '视频';
          else if (type === 'MOMENT_LINK') newTypeName = '链接';
          else if (type === 'MINIPROGRAM') newTypeName = '小程序';
          
          Modal.warning({
            title: '朋友圈附件限制',
            content: `朋友圈中图片、视频、链接、小程序只能四选一。当前已有${existingTypeName}附件，不能再添加${newTypeName}附件。请先删除现有的${existingTypeName}附件。`,
          });
          return;
        }
        
        // 检查是否已经有相同类型的附件（朋友圈中每种类型只能有一个）
        const sameTypeAttachment = currentMediaAttachments.find(att => att.type === type);
        if (sameTypeAttachment) {
          let typeName = '';
          if (type === 'MOMENT_IMAGE_GROUP') typeName = '图片';
          else if (type === 'MOMENT_VIDEO') typeName = '视频';
          else if (type === 'MOMENT_LINK') typeName = '链接';
          else if (type === 'MINIPROGRAM') typeName = '小程序';
          
          Modal.warning({
            title: '附件重复',
            content: `朋友圈中只能添加一个${typeName}附件。`,
          });
          return;
        }
      }
    }

    if (onAddAttachment) {
      onAddAttachment(type);
    }
  };

  // 计算总附件数量（图片集中每张图片都算一个，文字不算）
  const getTotalAttachmentCount = () => {
    let count = 0;
    attachments.forEach(attachment => {
      // 文字类型不算附件
      if (attachment.type === 'TEXT' || attachment.type === 'MOMENT_TEXT') {
        return;
      }
      
      if (['IMAGE_GROUP', 'MOMENT_IMAGE_GROUP'].includes(attachment.type)) {
        // 图片集中的每张图片都算一个附件
        if (attachment.imageGroup && attachment.imageGroup.mediaIds && attachment.imageGroup.mediaIds.length > 0) {
          count += attachment.imageGroup.mediaIds.length;
        }
        // 如果图片集为空或不存在，不计数
      } else {
        // 其他类型（视频、链接、小程序）每个都算1个附件
        count += 1;
      }
    });
    return count;
  };

  // 获取附件类型信息
  const getTypeInfo = (type) => {
    // 特殊处理文字类型
    if (type === 'TEXT' || type === 'MOMENT_TEXT') {
      return { label: '文字', icon: <MessageOutlined />, color: 'blue' };
    }
    
    return attachmentTypes.find(t => t.key === type) || { label: type, icon: <FileOutlined />, color: 'default' };
  };

  // 处理图片组变化时的附件数量检查
  const handleImageGroupChange = (attachmentId, newImageGroup) => {
    const currentAttachment = attachments.find(att => att.id === attachmentId);
    if (!currentAttachment) return;

    const currentImageCount = currentAttachment.imageGroup ? currentAttachment.imageGroup.mediaIds.length : 0;
    const newImageCount = newImageGroup.mediaIds.length;
    const difference = newImageCount - currentImageCount;
    
    // 计算其他附件的数量（不包括文字）
    const otherAttachmentsCount = attachments.reduce((count, att) => {
      if (att.id === attachmentId) return count; // 跳过当前图片组
      if (att.type === 'TEXT' || att.type === 'MOMENT_TEXT') return count; // 跳过文字类型
      
      if (['IMAGE_GROUP', 'MOMENT_IMAGE_GROUP'].includes(att.type)) {
        return count + (att.imageGroup ? att.imageGroup.mediaIds.length : 1);
      } else {
        return count + 1;
      }
    }, 0);

    const totalAfterChange = otherAttachmentsCount + newImageCount;

    if (totalAfterChange > attachmentLimit) {
      Modal.warning({
        title: '附件数量限制',
        content: `最多只能添加${attachmentLimit}个附件，当前已有${otherAttachmentsCount}个，再添加${difference}张图片将超出限制`,
      });
      return;
    }

    onUpdateContent(attachmentId, 'imageGroup', newImageGroup);
  };

  // 处理链接封面素材选择（新增）
  const handleSelectCoverFromMaterial = (attachmentId) => {
    setEditingAttachmentForCover(attachmentId);
    setCoverMaterialSelectorVisible(true);
  };

  // 处理封面素材选择结果（新增）
  const handleCoverMaterialSelect = async (material) => {
    try {
      console.log('选择封面素材:', material);

      // 调用后端API获取适配朋友圈的格式
      const sceneType = stepType === 'MOMENTS' ? 'moment' : 'message';
      const response = await api.get(`/materials/${material.id}/wework-format/${sceneType}`);

      if (response.data.success) {
        const weworkData = response.data.data;
        console.log('获取企微格式成功:', weworkData);

        // 更新附件的封面信息
        const currentAttachments = attachmentsRef.current;
        const updatedAttachments = currentAttachments.map(att => {
          if (att.id === editingAttachmentForCover) {
            return {
              ...att,
              picMaterialId: material.id,
              picMaterialName: material.name,
              picUrl_ossUrl: weworkData.image?.url || material.content?.image?.url,
              picMediaId: weworkData.image?.mediaId
            };
          }
          return att;
        });

        // 通过父组件更新（如果有提供更新方法）
        if (onUpdateContent) {
          onUpdateContent(editingAttachmentForCover, 'picMaterialId', material.id);
          onUpdateContent(editingAttachmentForCover, 'picMaterialName', material.name);
          onUpdateContent(editingAttachmentForCover, 'picUrl_ossUrl', weworkData.image?.url || material.content?.image?.url);
          onUpdateContent(editingAttachmentForCover, 'picMediaId', weworkData.image?.mediaId);
        }

        setCoverMaterialSelectorVisible(false);
        message.success('已设置链接封面');
      } else {
        message.error('获取素材数据失败');
      }
    } catch (error) {
      console.error('设置封面失败:', error);
      message.error('设置封面失败，请重试');
    }
  };

  // 渲染附件内容
  const renderAttachmentContent = (attachment) => {
    // 文本类型 - 直接显示可编辑的文本框
    if (attachment.type === 'TEXT' || attachment.type === 'MOMENT_TEXT') {
      return (
        <div style={{ padding: '8px 0' }}>
          <TextArea
            value={attachment.content || ''}
            onChange={(e) => onUpdateContent(attachment.id, 'content', e.target.value)}
            placeholder="请输入文本内容..."
            rows={3}
            disabled={isTemplateActive}
            style={{ resize: 'vertical' }}
          />
        </div>
      );
    }

    // 视频类型
    if (attachment.type === 'VIDEO' || attachment.type === 'MOMENT_VIDEO') {
      console.log('AttachmentManager渲染视频附件:', {
        attachmentId: attachment.id,
        mediaId: attachment.mediaId,
        ossUrl: attachment.ossUrl,
        attachmentObject: attachment
      });
      
      return (
        <div style={{ padding: '8px 0' }}>
          <MediaUpload
            fieldKey={attachment.id}
            mediaType="video"
            mediaId={attachment.mediaId}
            ossUrl={attachment.ossUrl}
            previewUrls={previewUrls}
            uploading={uploading}
            uploadProgress={uploadProgress}
            handleFileUpload={(options) => {
              // 包装原始的handleFileUpload，添加成功回调
              return handleFileUpload({
                ...options,
                onSuccess: (result) => {
                  console.log('视频上传成功:', result);
                  // 更新视频附件的mediaId和ossUrl
                  onUpdateContent(attachment.id, 'mediaId', result.mediaId);
                  onUpdateContent(attachment.id, 'ossUrl', result.ossUrl);
                  
                  // 调用原始的成功回调
                  if (options.onSuccess) {
                    options.onSuccess(result);
                  }
                }
              });
            }}
            handleRemoveFile={(fieldKey) => {
              console.log('移除视频, fieldKey:', fieldKey);
              // 清除视频数据
              onUpdateContent(attachment.id, 'mediaId', '');
              onUpdateContent(attachment.id, 'ossUrl', '');
              if (handleRemoveFile) handleRemoveFile(fieldKey, stepType);
            }}
            stepType={stepType}
            disabled={isTemplateActive}
            hideRemoveButton={true}
            hideReuploadButton={true}
          />
        </div>
      );
    }

    // 图片组类型 - 使用自定义图片选择器
    if (['IMAGE_GROUP', 'MOMENT_IMAGE_GROUP'].includes(attachment.type)) {
      const imageGroup = attachment.imageGroup || { mediaIds: [], ossUrls: {} };
      
      return (
        <div style={{ padding: '8px 0' }}>
          <CustomImageSelector
            imageGroup={imageGroup}
            onImageGroupChange={(newImageGroup) => {
              // 先进行附件数量检查
              const currentImageCount = imageGroup.mediaIds.length;
              const newImageCount = newImageGroup.mediaIds.length;
              const difference = newImageCount - currentImageCount;
              
              if (difference > 0) {
                // 计算其他附件的数量（不包括文字和当前图片组）
                const otherAttachmentsCount = attachments.reduce((count, att) => {
                  if (att.id === attachment.id) return count; // 跳过当前图片组
                  if (att.type === 'TEXT' || att.type === 'MOMENT_TEXT') return count; // 跳过文字类型
                  
                  if (['IMAGE_GROUP', 'MOMENT_IMAGE_GROUP'].includes(att.type)) {
                    return count + (att.imageGroup ? att.imageGroup.mediaIds.length : 1);
                  } else {
                    return count + 1;
                  }
                }, 0);

                const totalAfterChange = otherAttachmentsCount + newImageCount;

                if (totalAfterChange > attachmentLimit) {
                  Modal.warning({
                    title: '附件数量限制',
                    content: `最多只能添加${attachmentLimit}个附件，当前已有${otherAttachmentsCount}个，再添加${difference}张图片将超出限制`,
                  });
                  return;
                }
              }

              // 更新图片组
              onUpdateContent(attachment.id, 'imageGroup', newImageGroup);
            }}
            disabled={isTemplateActive}
            stepType={stepType}
            maxCount={attachmentLimit}
            handleFileUpload={handleFileUpload}
            handleRemoveFile={handleRemoveFile}
            previewUrls={previewUrls}
            uploading={uploading}
            uploadProgress={uploadProgress}
            fieldKey={attachment.id}
            getTotalAttachmentCount={getTotalAttachmentCount}
            handleUpdatePreviewUrls={handleUpdatePreviewUrls}
          />
        </div>
      );
    }

    // 链接类型 - 直接显示表单
    if (attachment.type === 'LINK' || attachment.type === 'MOMENT_LINK') {
      const isMomentLink = attachment.type === 'MOMENT_LINK';

      return (
        <div style={{ padding: '8px 0' }}>
          <Form layout="vertical" size="small">
            <Form.Item label="标题" style={{ marginBottom: 12 }}>
              <Input
                value={attachment.title || ''}
                onChange={(e) => onUpdateContent(attachment.id, 'title', e.target.value)}
                placeholder="请输入链接标题"
                disabled={isTemplateActive}
              />
            </Form.Item>
            <Form.Item label="描述" style={{ marginBottom: 12 }}>
              <Input
                value={attachment.description || ''}
                onChange={(e) => onUpdateContent(attachment.id, 'description', e.target.value)}
                placeholder="请输入链接描述"
                disabled={isTemplateActive}
              />
            </Form.Item>
            <Form.Item label="链接地址" style={{ marginBottom: isMomentLink ? 12 : 0 }}>
              <Input
                value={attachment.url || ''}
                onChange={(e) => onUpdateContent(attachment.id, 'url', e.target.value)}
                placeholder="https://example.com"
                disabled={isTemplateActive}
              />
            </Form.Item>

            {/* 朋友圈链接需要封面图 */}
            {isMomentLink && (
              <Form.Item
                label="链接封面"
                required
                style={{ marginBottom: 0 }}
              >
                {attachment.picMaterialId ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px', border: '1px solid #d9d9d9', borderRadius: 4 }}>
                    <Image
                      src={attachment.picUrl_ossUrl}
                      width={80}
                      height={80}
                      style={{ objectFit: 'cover', borderRadius: 4 }}
                      fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 500, marginBottom: 4 }}>{attachment.picMaterialName || '已选择封面图'}</div>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }} ellipsis>
                        {attachment.picUrl_ossUrl}
                      </Typography.Text>
                    </div>
                    <Button
                      size="small"
                      onClick={() => handleSelectCoverFromMaterial(attachment.id)}
                      disabled={isTemplateActive}
                    >
                      重新选择
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="dashed"
                    icon={<FileImageOutlined />}
                    onClick={() => handleSelectCoverFromMaterial(attachment.id)}
                    block
                    disabled={isTemplateActive}
                    style={{ height: 80, fontSize: 14 }}
                  >
                    从素材库选择封面图片（必选）
                  </Button>
                )}
              </Form.Item>
            )}
          </Form>
        </div>
      );
    }

    // 小程序类型 - 直接显示表单
    if (attachment.type === 'MINIPROGRAM') {
      // 处理小程序标题输入，实时检查字节长度
      const handleMiniprogramTitleChange = (e) => {
        const value = e.target.value;
        const byteLength = getByteLength(value);
        
        // 如果超过64字节，截断到64字节
        if (byteLength > 64) {
          // 逐字符检查，找到不超过64字节的最大长度
          let truncatedValue = '';
          for (let i = 0; i < value.length; i++) {
            const testValue = value.substring(0, i + 1);
            if (getByteLength(testValue) <= 64) {
              truncatedValue = testValue;
            } else {
              break;
            }
          }
          onUpdateContent(attachment.id, 'title', truncatedValue);
        } else {
          onUpdateContent(attachment.id, 'title', value);
        }
      };

      return (
        <div style={{ padding: '8px 0' }}>
          <Form layout="vertical" size="small">
            <Form.Item 
              label="标题" 
              style={{ marginBottom: 12 }}
            >
              <Input
                value={attachment.title || ''}
                onChange={(e) => onUpdateContent(attachment.id, 'title', (e.target.value || '').slice(0, 20))}
                placeholder="请输入小程序标题（最多20字）"
                disabled={isTemplateActive}
                showCount
                maxLength={20}
              />
            </Form.Item>
            <Form.Item label="小程序AppID" style={{ marginBottom: 12 }}>
              <Input
                value={attachment.appid || ''}
                onChange={(e) => onUpdateContent(attachment.id, 'appid', e.target.value)}
                placeholder="请输入小程序AppID"
                disabled={isTemplateActive}
              />
            </Form.Item>
            <Form.Item label="页面路径" style={{ marginBottom: 12 }}>
              <Input
                value={attachment.pagepath || ''}
                onChange={(e) => {
                  let value = e.target.value;
                  // 自动补全开头的斜杠
                  if (value && !value.startsWith('/')) {
                    value = '/' + value;
                  }
                  onUpdateContent(attachment.id, 'pagepath', value);
                }}
                placeholder="/pages/index"
                disabled={isTemplateActive}
              />
            </Form.Item>
            
            {/* 小程序缩略图 */}
            <Form.Item label="缩略图" style={{ marginBottom: 0 }}>
              <MediaUpload
                fieldKey={`${attachment.id}_thumb`}
                mediaType="image"
                mediaId={attachment.thumbMediaId}
                ossUrl={attachment.thumbMediaId_ossUrl}
                previewUrls={previewUrls}
                uploading={uploading}
                uploadProgress={uploadProgress}
                handleFileUpload={(options) => {
                  // 包装原始的handleFileUpload，添加成功回调
                  return handleFileUpload({
                    ...options,
                    onSuccess: (result) => {
                      console.log('小程序缩略图上传成功:', result);
                      // 更新小程序附件的缩略图信息
                      onUpdateContent(attachment.id, 'thumbMediaId', result.mediaId);
                      onUpdateContent(attachment.id, 'thumbMediaId_ossUrl', result.ossUrl);
                      
                      // 直接更新全局预览URLs，确保MediaUpload组件能收到更新
                      if (typeof handleUpdatePreviewUrls === 'function') {
                        console.log('直接更新小程序缩略图全局预览URLs:', { [result.mediaId]: result.ossUrl });
                        handleUpdatePreviewUrls({ [result.mediaId]: result.ossUrl });
                      }
                      
                      // 调用原始的成功回调
                      if (options.onSuccess) {
                        options.onSuccess(result);
                      }
                    }
                  });
                }}
                handleRemoveFile={(fieldKey) => {
                  console.log('移除小程序缩略图, fieldKey:', fieldKey);
                  // 移除缩略图
                  onUpdateContent(attachment.id, 'thumbMediaId', '');
                  onUpdateContent(attachment.id, 'thumbMediaId_ossUrl', '');
                  if (handleRemoveFile) handleRemoveFile(fieldKey, stepType);
                }}
                stepType={stepType}
                disabled={isTemplateActive}
                hideRemoveButton={true}
                hideReuploadButton={true}
              />
            </Form.Item>
          </Form>
        </div>
      );
    }

    return null;
  };

  // 计算总附件数用于显示
  const totalAttachmentCount = getTotalAttachmentCount();

  // 确保总是有一个文本附件存在
  useEffect(() => {
    if (attachments.length === 0 || !attachments.some(att => att.type === 'TEXT' || att.type === 'MOMENT_TEXT')) {
      // 如果没有附件或没有文本附件，添加一个默认的文本附件
      const textType = stepType === 'MESSAGE' ? 'TEXT' : 'MOMENT_TEXT';
      onAddAttachment && onAddAttachment(textType);
    }
  }, [attachments.length, stepType, onAddAttachment]);

  // 处理素材填充
  useEffect(() => {
    if (currentAttachmentId === 'PENDING_FILL' && window._pendingMaterialData) {
      console.log('useEffect检测到素材填充需求:', {
        currentAttachmentId,
        pendingData: window._pendingMaterialData,
        attachmentsCount: attachments.length
      });
      
      const { material, attachmentType } = window._pendingMaterialData;
      
      // 添加短暂延迟，确保能获取到最新的attachments状态
      setTimeout(() => {
        console.log('延迟后开始素材填充，当前attachments数量:', attachments.length);
        
        // 查找最新创建的同类型附件
        const sameTypeAttachments = attachments.filter(att => att.type === attachmentType);
        if (sameTypeAttachments.length > 0) {
          const newAttachment = sameTypeAttachments[sameTypeAttachments.length - 1];
          
          console.log('找到目标附件进行填充:', {
            attachmentId: newAttachment.id,
            attachmentType: newAttachment.type,
            totalSameType: sameTypeAttachments.length
          });
          
          // 清理临时数据
          setCurrentAttachmentId(null);
          delete window._pendingMaterialData;
          
          // 填充素材数据
          fillAttachmentWithMaterialData(material, newAttachment.id);
        } else {
          console.warn('延迟后仍未找到同类型附件，继续等待...');
          // 如果还没找到，可能需要再等一会儿（最多重试几次）
          const retryCount = window._retryCount || 0;
          if (retryCount < 3) {
            window._retryCount = retryCount + 1;
            // 不清理临时数据，让下次useEffect再尝试
          } else {
            console.error('多次重试后仍未找到目标附件，放弃填充');
            setCurrentAttachmentId(null);
            delete window._pendingMaterialData;
            delete window._retryCount;
          }
        }
      }, 10); // 减少延迟到10ms
    }
  }, [attachments, currentAttachmentId]);

  return (
    <div className="attachment-manager">
      <div style={{ marginBottom: 16 }}>
          {/* 文本框始终显示在顶部 */}
          {(() => {
            const textType = stepType === 'MESSAGE' ? 'TEXT' : 'MOMENT_TEXT';
            const textAttachment = attachments.find(att => att.type === textType);
            
            if (textAttachment) {
              return (
                <div style={{ marginBottom: 16 }}>
                  <TextArea
                    value={textAttachment.content || ''}
                    onChange={(e) => onUpdateContent(textAttachment.id, 'content', e.target.value)}
                    placeholder="请输入文本内容..."
                    rows={5}
                    disabled={isTemplateActive}
                    style={{ resize: 'vertical' }}
                  />
                </div>
              );
            }
            return null;
          })()}
          
          {/* 按钮区域 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px'}}>
            {/* 添加文字素材超链接 - 放在最左边 */}
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Button
                type="link"
                disabled={isTemplateActive}
                onClick={() => {
                  setMaterialSelectorVisible(true);
                  setMaterialType('TEXT'); // 自动选中文字标签
                }}
                style={{ textAlign: 'left', padding: 0, height: 'auto' }}
              >
                添加文字素材
              </Button>
            </div>
            
            {/* 分割线 */}
            <Divider style={{ margin: '0', width: '160px' }} />
            
            {/* 添加附件素材 - 加号图标 + 提示文字 */}
            <div 
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                cursor: isTemplateActive || totalAttachmentCount >= attachmentLimit ? 'not-allowed' : 'pointer',
                opacity: isTemplateActive || totalAttachmentCount >= attachmentLimit ? 0.5 : 1
              }}
              onClick={() => {
                if (!isTemplateActive && totalAttachmentCount < attachmentLimit) {
                  setMaterialSelectorVisible(true);
                  setMaterialType(null); // 不预选标签
                }
              }}
            >
              <PlusOutlined style={{ color: '#1677FF', fontSize: '16px' }} />
              <span style={{ marginLeft: '8px', color: '#1677FF', fontSize: '14px' }}>添加图片/视频/小程序等附件</span>
            </div>
          </div>
        </div>
        
        {attachments.filter(att => att.type !== 'TEXT' && att.type !== 'MOMENT_TEXT').length > 0 && (
          <div className="attachment-simple-list">
            {attachments.filter(att => att.type !== 'TEXT' && att.type !== 'MOMENT_TEXT').map((attachment, index) => {
              const typeInfo = getTypeInfo(attachment.type);
              
              return (
                <div
                  key={attachment.id}
                  className="attachment-simple-item"
                  style={{ 
                    marginBottom: index < attachments.filter(att => att.type !== 'TEXT' && att.type !== 'MOMENT_TEXT').length - 1 ? 16 : 0
                  }}
                >
                  <div className="attachment-simple-header">
                    <div className="attachment-simple-title">
                      <span style={{ fontWeight: 500, fontSize: '14px' }}>{typeInfo.label}</span>
                      {['IMAGE_GROUP', 'MOMENT_IMAGE_GROUP'].includes(attachment.type) && 
                       attachment.imageGroup && attachment.imageGroup.mediaIds && 
                       attachment.imageGroup.mediaIds.length > 0 && (
                        <Tag size="small" color="green" style={{ marginLeft: '8px' }}>
                          {attachment.imageGroup.mediaIds.length}张图片
                        </Tag>
                      )}
                    </div>
                    {!isTemplateActive && (attachment.type !== 'TEXT' && attachment.type !== 'MOMENT_TEXT') && (
                      <Button 
                        type="text" 
                        size="small" 
                        danger 
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          let deleteCount = 1; // 默认删除1个附件
                          let deleteMessage = '';
                          
                          if (['IMAGE_GROUP', 'MOMENT_IMAGE_GROUP'].includes(attachment.type) && 
                              attachment.imageGroup && attachment.imageGroup.mediaIds && 
                              attachment.imageGroup.mediaIds.length > 0) {
                            // 图片集计算实际图片数量
                            deleteCount = attachment.imageGroup.mediaIds.length;
                            deleteMessage = `（将删除${deleteCount}个附件）`;
                          }
                          
                          Modal.confirm({
                            title: '确认删除',
                            content: `确定要删除这个${typeInfo.label}吗？${deleteMessage}`,
                            onOk: () => onRemoveAttachment && onRemoveAttachment(attachment.id),
                            okText: '删除',
                            cancelText: '取消',
                            okButtonProps: { danger: true }
                          });
                        }}
                      >
                        删除
                      </Button>
                    )}
                  </div>
                  <div className="attachment-simple-content">
                    {renderAttachmentContent(attachment)}
                  </div>
                </div>
              );
            })}
            
            {/* 继续添加附件按钮 - 位于列表最下方 */}
            <div style={{ marginTop: 16, paddingTop: 12 }}>
              <Button
                type="link"
                disabled={isTemplateActive || totalAttachmentCount >= attachmentLimit}
                onClick={() => {
                  setMaterialSelectorVisible(true);
                  setMaterialType(null); // 不预选标签
                }}
                style={{ padding: 0, height: 'auto' }}
              >
                <PlusOutlined style={{ color: '#1677FF', fontSize: '16px' }} />
                继续添加附件
              </Button>
            </div>
          </div>
        )}
      
      {/* 素材选择器 */}
      <MaterialSelector
        visible={materialSelectorVisible}
        onCancel={() => {
          setMaterialSelectorVisible(false);
          setCurrentAttachmentId(null);
          setMaterialType(null);
        }}
        onSelect={handleMaterialSelect}
        materialType={materialType} // 传递当前设置的素材类型以自动选中标签
        hideAllTypesTab={materialType === 'TEXT'} // 当选择文字素材时隐藏全部类型tab
        multiSelect={typeof materialSelectorMultiSelect === 'boolean' ? materialSelectorMultiSelect : true}
        title="选择素材"
        allowUpload={true}
        onUpload={(selectedMaterialType) => {
          setUploadMaterialType(selectedMaterialType || 'TEXT');
          setMaterialUploadVisible(true);
        }}
        refreshTrigger={refreshTrigger}
        tenantId={getTenantId()}
      />

      {/* 封面素材选择器（新增） */}
      <MaterialSelector
        visible={coverMaterialSelectorVisible}
        onCancel={() => {
          setCoverMaterialSelectorVisible(false);
          setEditingAttachmentForCover(null);
        }}
        onSelect={handleCoverMaterialSelect}
        materialType="IMAGE"
        multiSelect={false}
        title="选择链接封面图片"
        allowUpload={true}
        onUpload={(selectedMaterialType) => {
          setUploadMaterialType('IMAGE');
          setMaterialUploadVisible(true);
        }}
        refreshTrigger={refreshTrigger}
        tenantId={getTenantId()}
      />

      {/* 素材上传Modal */}
      <MaterialUploadModal
        visible={materialUploadVisible}
        onCancel={() => setMaterialUploadVisible(false)}
        onSuccess={() => {
          // 上传成功后关闭Modal并刷新素材列表
          setMaterialUploadVisible(false);
          // 触发MaterialSelector刷新
          setRefreshTrigger(prev => prev + 1);
        }}
        initialType={uploadMaterialType}
        hideTypeSelection={true}
        tenantId={getTenantId()}
      />
    </div>
  );

  // 异步顺序处理多个素材
  async function handleMultipleMaterialsSequentially(materials) {
    console.log('开始顺序处理多个素材:', materials.length);
    
    // 检查是否都是图片类型
    const allImages = materials.every(material => material.materialType === 'IMAGE');
    
    if (allImages && materials.length > 1) {
      // 多张图片的特殊处理：先创建图片组，再添加所有图片
      console.log('检测到多张图片，使用特殊处理逻辑');
      await handleMultipleImages(materials);
    } else {
      // 其他情况：逐个处理
      for (let i = 0; i < materials.length; i++) {
        const material = materials[i];
        console.log(`处理第${i + 1}/${materials.length}个素材:`, material.name);
        
        // 异步处理单个素材，等待完成后再处理下一个
        try {
          handleSingleMaterialSelect(material);
          // 给状态更新一些时间
          await new Promise(resolve => setTimeout(resolve, 100));
          console.log(`✅ 第${i + 1}个素材处理完成:`, material.name);
        } catch (error) {
          console.error(`❌ 第${i + 1}个素材处理失败:`, material.name, error);
        }
      }
    }
    
    console.log('所有素材处理完成');
  }
  
  // 批量添加图片到现有图片组，保持顺序
  async function handleBatchAddToImageGroup(materials, targetImageGroupId, attachmentType) {
    console.log('🖼️ 开始批量添加图片到现有图片组，保持原始顺序');
    
    // 获取所有图片的数据
    const imageDataList = [];
    for (let i = 0; i < materials.length; i++) {
      const material = materials[i];
      console.log(`🖼️ 处理第${i + 1}/${materials.length}张图片数据:`, material.name, '原始索引:', i);
      
      try {
        // 获取素材的企微格式数据
        let weworkFormatResponse;
        if (stepType === 'MOMENTS') {
          const response = await api.post(`/materials/${material.id}/to-wework?sceneType=moment`);
          weworkFormatResponse = response.data;
        } else {
          // 明确指定message场景，避免使用废弃的默认值
          const response = await api.post(`/materials/${material.id}/to-wework?sceneType=message`);
          weworkFormatResponse = response.data;
        }
        
        if (weworkFormatResponse.success && weworkFormatResponse.data) {
          const weworkMaterial = weworkFormatResponse.data;
          const imageData = weworkMaterial.image || weworkMaterial.imageInfo;
          const imageMediaId = imageData?.mediaId || imageData?.media_id;
          const imageUrl = imageData?.url || imageData?.pic_url;
          
          console.log(`🔍 AttachmentManager - 素材${i + 1}处理调试:`, {
            materialId: material.id,
            materialName: material.name,
            weworkMaterial,
            imageData,
            extractedMediaId: imageMediaId,
            extractedUrl: imageUrl,
            isMediaIdValid: !(!imageMediaId || imageMediaId.startsWith('temp_'))
          });
          
          if (imageData && imageUrl) {
            // 检查是否有有效的mediaId
            if (!imageMediaId || imageMediaId.startsWith('temp_')) {
              const materialName = material.name || '未命名素材';
              message.warning(`素材"${materialName}"尚未上传完毕，请稍后刷新重试`);
              return; // 直接返回，不添加这个素材
            }
            
            const effectiveMediaId = imageMediaId;
            
            imageDataList.push({
              originalIndex: i,
              effectiveMediaId,
              imageUrl,
              materialName: material.name
            });
            
            console.log(`✅ 第${i + 1}张图片数据处理成功:`, { effectiveMediaId, imageUrl, materialName: material.name });
          } else {
            console.error(`❌ 第${i + 1}张图片数据无效:`, { imageData, imageUrl });
          }
        } else {
          console.error(`❌ 第${i + 1}张图片API调用失败:`, weworkFormatResponse);
        }
      } catch (error) {
        console.error(`❌ 第${i + 1}张图片处理异常:`, error);
      }
    }
    
    if (imageDataList.length === 0) {
      console.error('❌ 没有有效的图片数据，无法添加');
      return;
    }
    
    // 按原始索引排序确保顺序正确
    imageDataList.sort((a, b) => a.originalIndex - b.originalIndex);
    
    console.log('🖼️ 最终图片添加顺序:', imageDataList.map((img, i) => `${i + 1}. ${img.materialName} (原始索引: ${img.originalIndex})`));
    
    // 获取当前图片组状态
    const currentAttachments = attachmentsRef.current;
    const targetAttachment = currentAttachments.find(att => att.id === targetImageGroupId);
    
    if (!targetAttachment) {
      console.error('❌ 找不到目标图片组:', targetImageGroupId);
      return;
    }
    
    const currentImageGroup = targetAttachment.imageGroup || { mediaIds: [], ossUrls: {} };
    
    // 一次性添加所有图片，保持顺序
    const newMediaIds = [...currentImageGroup.mediaIds];
    const newOssUrls = { ...currentImageGroup.ossUrls };
    
    imageDataList.forEach((imageData) => {
      newMediaIds.push(imageData.effectiveMediaId);
      newOssUrls[imageData.effectiveMediaId] = imageData.imageUrl;
    });
    
    const newImageGroup = {
      mediaIds: newMediaIds,
      ossUrls: newOssUrls
    };
    
    console.log('🖼️ 批量更新图片组:', {
      oldCount: currentImageGroup.mediaIds.length,
      newCount: newImageGroup.mediaIds.length,
      addedCount: imageDataList.length,
      finalOrder: newImageGroup.mediaIds
    });
    
    // 一次性更新图片组
    onUpdateContent(targetImageGroupId, 'imageGroup', newImageGroup);
    
    console.log('✅ 批量添加图片完成');
  }

  // 处理多张图片
  async function handleMultipleImages(materials) {
    console.log('🖼️ 处理多张图片:', materials.length);
    console.log('🖼️ 图片处理顺序:', materials.map((m, i) => `${i + 1}. ${m.name || m.id || '未知'}`));
    
    // 确定图片组类型
    const attachmentType = stepType === 'MOMENTS' ? 'MOMENT_IMAGE_GROUP' : 'IMAGE_GROUP';
    
    // 检查是否已有图片组
    const existingImageGroup = attachments.find(att => att.type === attachmentType);
    
    if (existingImageGroup) {
      console.log('找到现有图片组，批量添加图片以保持顺序');
      // 如果已有图片组，批量添加所有图片以保持顺序
      await handleBatchAddToImageGroup(materials, existingImageGroup.id, attachmentType);
    } else {
      console.log('创建新图片组并添加所有图片');
      // 检查附件数量限制
      const currentCount = getTotalAttachmentCount();
      if (currentCount >= attachmentLimit) {
        Modal.warning({
          title: '附件数量限制',
          content: `最多只能添加${attachmentLimit}个附件`
        });
        return;
      }
      
      // 朋友圈类型检查
      if (stepType === 'MOMENTS') {
        const mediaAttachmentTypes = ['MOMENT_IMAGE_GROUP', 'MOMENT_VIDEO', 'MOMENT_LINK', 'MINIPROGRAM'];
        const currentMediaAttachments = attachments.filter(att => 
          mediaAttachmentTypes.includes(att.type)
        );
        
        if (currentMediaAttachments.length > 0) {
          Modal.warning({
            title: '朋友圈附件限制',
            content: '朋友圈中图片、视频、链接、小程序只能四选一，请先删除现有附件'
          });
          return;
        }
      }
      
      // 创建新图片组
      const newAttachmentId = onAddAttachment(attachmentType);
      
      if (newAttachmentId) {
        console.log('成功创建新图片组，ID:', newAttachmentId);
        // 等待附件创建完成
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // 逐个添加所有图片到新创建的图片组
        for (let i = 0; i < materials.length; i++) {
          const material = materials[i];
          console.log(`添加第${i + 1}/${materials.length}张图片到新图片组:`, material.name);
          
          // 正确等待异步函数完成
          try {
            await fillAttachmentWithMaterialData(material, newAttachmentId, attachmentType);
            console.log(`✅ 第${i + 1}张图片添加完成:`, material.name);
          } catch (error) {
            console.error(`❌ 第${i + 1}张图片添加失败:`, material.name, error);
          }
        }
      } else {
        console.log('创建图片组失败，回退到逐个处理');
        // 回退到原来的逻辑
        for (let i = 0; i < materials.length; i++) {
          const material = materials[i];
          console.log(`回退处理第${i + 1}/${materials.length}张图片:`, material.name);
          try {
            handleSingleMaterialSelect(material);
            // 给状态更新一些时间
            await new Promise(resolve => setTimeout(resolve, 100));
          } catch (error) {
            console.error(`❌ 回退处理第${i + 1}张图片失败:`, material.name, error);
          }
        }
      }
    }
  }

  // 处理素材选择
  function handleMaterialSelect(materialOrArray) {
    console.log('🎯 AttachmentManager - 选择了素材:', materialOrArray);
    
    // 如果是数组，打印选择顺序
    if (Array.isArray(materialOrArray)) {
      console.log('🎯 AttachmentManager - 素材选择顺序:', materialOrArray.map((m, i) => `${i + 1}. ${m.name || m.id || '未知'}`));
    }
    
    // 处理多选情况
    if (Array.isArray(materialOrArray)) {
      // 多选素材，异步顺序处理以避免状态竞争
      handleMultipleMaterialsSequentially(materialOrArray);
      setMaterialSelectorVisible(false);
      return;
    }
    
    // 单选素材
    handleSingleMaterialSelect(materialOrArray);
    setMaterialSelectorVisible(false);
  }
  
  // 处理单个素材选择
  function handleSingleMaterialSelect(material) {
    // 确定附件类型
    let attachmentType;
    const materialType = material.materialType || material.msgtype;
    
    switch (materialType) {
      case 'IMAGE':
      case 'image':
        attachmentType = stepType === 'MOMENTS' ? 'MOMENT_IMAGE_GROUP' : 'IMAGE_GROUP';
        break;
      case 'VIDEO':
      case 'video':
        attachmentType = stepType === 'MOMENTS' ? 'MOMENT_VIDEO' : 'VIDEO';
        break;
      case 'LINK':
      case 'link':
      case 'news':
        attachmentType = stepType === 'MOMENTS' ? 'MOMENT_LINK' : 'LINK';
        break;
      case 'MINIPROGRAM':
      case 'miniprogram':
        attachmentType = 'MINIPROGRAM';
        break;
      case 'TEXT':
      case 'text':
        // 对于文本类型，插入到现有的文本附件中
        insertTextToExistingAttachment(material.textContent || '');
        return;
      default:
        console.warn('不支持的素材类型:', materialType);
        Modal.warning({
          title: '不支持的素材类型',
          content: `素材类型 ${materialType} 暂不支持`
        });
        return;
    }

    console.log('确定附件类型:', attachmentType);

    // 特殊处理图片素材：如果有指定的currentAttachmentId或已经有图片组，直接添加到现有图片组
    if (attachmentType === 'IMAGE_GROUP' || attachmentType === 'MOMENT_IMAGE_GROUP') {
      let targetImageGroup = null;
      
      // 优先使用指定的currentAttachmentId
      if (currentAttachmentId) {
        targetImageGroup = attachments.find(att => att.id === currentAttachmentId && att.type === attachmentType);
        console.log('🖼️ 使用指定的图片组:', currentAttachmentId);
      } else {
        // 如果没有指定，查找现有的图片组
        targetImageGroup = attachments.find(att => att.type === attachmentType);
        console.log('🖼️ 找到现有图片组:', targetImageGroup?.id);
      }
      
      if (targetImageGroup) {
        console.log('🖼️ 添加图片到现有图片组，使用跨场景转换逻辑填充:', targetImageGroup.id);
        // 使用带跨场景转换功能的方法
        fillAttachmentWithMaterialData(material, targetImageGroup.id, attachmentType);
        // 清除currentAttachmentId
        setCurrentAttachmentId(null);
        return;
      }
    }

    // 检查附件数量限制
    const currentCount = getTotalAttachmentCount();
    if (currentCount >= 9) {
      Modal.warning({
        title: '附件数量限制',
        content: '最多只能添加9个附件'
      });
      return;
    }

    // 朋友圈类型的特殊检查：图片、视频、链接、小程序只能四选一
    if (stepType === 'MOMENTS') {
      const mediaAttachmentTypes = ['MOMENT_IMAGE_GROUP', 'MOMENT_VIDEO', 'MOMENT_LINK', 'MINIPROGRAM'];
      const currentMediaAttachments = attachments.filter(att => 
        mediaAttachmentTypes.includes(att.type)
      );
      
      if (mediaAttachmentTypes.includes(attachmentType) && currentMediaAttachments.length > 0) {
        const existingType = currentMediaAttachments[0].type;
        if (existingType !== attachmentType) {
          Modal.warning({
            title: '朋友圈附件限制',
            content: '朋友圈中图片、视频、链接、小程序只能四选一，请先删除现有附件'
          });
          return;
        }
      }
    }

    // 创建新附件
    const newAttachmentId = onAddAttachment(attachmentType);
    
    if (newAttachmentId) {
      // 如果onAddAttachment返回了ID，使用指定的ID填充素材
      console.log('成功创建新附件，ID:', newAttachmentId);
      
      // 使用nextTick或requestAnimationFrame来确保状态更新，而不是固定延迟
      requestAnimationFrame(() => {
        console.log('开始填充素材数据到附件:', newAttachmentId);
        fillAttachmentWithMaterialData(material, newAttachmentId);
      });
    } else {
      // 如果onAddAttachment没有返回ID（向后兼容），使用原来的逻辑
      console.log('onAddAttachment没有返回ID，使用类型查找最新附件');
      
      // 对于向后兼容的情况，稍微延迟以确保附件创建完成
      setTimeout(() => {
        console.log('开始通过类型查找并填充素材数据');
        fillAttachmentWithMaterialData(material, null, attachmentType);
      }, 20); // 减少延迟到20ms
    }
  }
  
  // 验证朋友圈素材是否符合微信朋友圈API要求
  function validateMomentsAttachment(material, materialType) {
    const materialData = material.imageInfo || material.videoInfo || material.image || material.video || material;
    const fileSize = materialData?.fileSize || materialData?.size;
    const fileName = materialData?.fileName || material.name || '';
    const url = materialData?.url || materialData?.pic_url;
    
    // 通用大小限制：10MB
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (fileSize && fileSize > maxSize) {
      throw new Error(`文件大小超出限制，朋友圈素材最大支持10MB，当前文件：${(fileSize / 1024 / 1024).toFixed(2)}MB`);
    }
    
    switch (materialType) {
      case 'IMAGE':
      case 'image':
        // 验证图片尺寸 - 使用预存的尺寸信息
        const width = materialData?.width || materialData?.imageWidth;
        const height = materialData?.height || materialData?.imageHeight;
        
        if (width && height) {
          // 如果有尺寸信息，直接验证
          const longSide = Math.max(width, height);
          const shortSide = Math.min(width, height);
          
          if (longSide > 10800) {
            throw new Error(`图片尺寸超出限制，朋友圈图片长边不能超过10800像素，当前长边：${longSide}像素`);
          }
          if (shortSide > 1080) {
            throw new Error(`图片尺寸超出限制，朋友圈图片短边不能超过1080像素，当前短边：${shortSide}像素`);
          }
        } else {
          // 如果没有尺寸信息，记录警告但不阻塞导入
          console.warn('图片缺少尺寸信息，无法验证朋友圈尺寸要求，素材已导入，请注意发送时可能会被微信拒绝。');
        }
        break;
        
      case 'VIDEO':
      case 'video':
        // 验证视频时长（如果提供了时长信息）
        const duration = materialData?.duration || materialData?.video_duration;
        if (duration && duration > 30) {
          throw new Error(`视频时长超出限制，朋友圈视频时长不能超过30秒，当前时长：${duration}秒`);
        }
        break;
        
      case 'FILE':
      case 'file':
        // 文件类型只验证大小，格式由微信API决定
        break;
        
      default:
        // 其他类型不需要特殊验证
        break;
    }
  }
  

  // 填充素材数据的独立函数
  async function fillAttachmentWithMaterialData(material, targetAttachmentId, attachmentType = null) {
    console.log(`🔍 [DEBUG] fillAttachmentWithMaterialData 开始执行:`, {
      stepType,
      materialId: material.id,
      materialType: material.materialType,
      targetAttachmentId,
      attachmentType
    });
    
    try {
      // 如果是朋友圈场景，首先验证素材是否符合朋友圈API要求
      if (stepType === 'MOMENTS') {
        const materialType = material.materialType || material.msgtype;
        try {
          validateMomentsAttachment(material, materialType);
          console.log('✅ 朋友圈素材验证通过');
        } catch (validationError) {
          console.error('❌ 朋友圈素材验证失败:', validationError.message);
          Modal.error({
            title: '素材不符合朋友圈要求',
            content: validationError.message,
            width: 500
          });
          return; // 验证失败，停止执行
        }
      }
      
      let weworkFormatResponse;
      
      // 朋友圈场景使用带场景类型的新端点，其他场景使用原有端点保证兼容性
      if (stepType === 'MOMENTS') {
        console.log(`📄 朋友圈场景：调用新端点获取moment场景素材数据，素材ID: ${material.id}`);
        weworkFormatResponse = await api.get(`/materials/${material.id}/wework-format/moment`);
        console.log(`✅ 朋友圈场景API调用完成，响应:`, weworkFormatResponse.data);
      } else {
        console.log(`📄 消息场景：使用原有端点保证兼容性，stepType: ${stepType}`);
        weworkFormatResponse = await api.get(`/materials/${material.id}/wework-format`);
        console.log(`✅ 消息场景API调用完成，响应:`, weworkFormatResponse.data);
      }
      
      if (weworkFormatResponse.data.success) {
        const weworkMaterial = weworkFormatResponse.data.data;
        console.log(`📄 成功获取素材数据:`, weworkMaterial);
        // 使用转换后的数据填充附件
        fillAttachmentWithMaterial(weworkMaterial, attachmentType, targetAttachmentId);
      } else {
        console.warn(`⚠️ 获取素材数据失败，回退到原始数据`);
        // 回退到使用原始素材数据
        fillAttachmentWithMaterial(material, attachmentType, targetAttachmentId);
      }
    } catch (apiError) {
      console.warn('⚠️ API调用失败，回退到原始素材数据:', apiError);
      // 回退到使用原始素材数据
      fillAttachmentWithMaterial(material, attachmentType, targetAttachmentId);
    }
  }
  
  // 将文字素材插入到现有的文本附件中
  function insertTextToExistingAttachment(text) {
    const textType = stepType === 'MESSAGE' ? 'TEXT' : 'MOMENT_TEXT';
    const textAttachment = attachments.find(att => att.type === textType);

    if (textAttachment) {
      // 直接替换内容，而不是追加
      onUpdateContent(textAttachment.id, 'content', text);
    } else {
      // 如果没有文本附件，先创建一个
      if (onAddAttachment) {
        onAddAttachment(textType);
        // 等待创建完成后再插入文字
        setTimeout(() => {
          const newTextAttachment = attachments.find(att => att.type === textType);
          if (newTextAttachment) {
            onUpdateContent(newTextAttachment.id, 'content', text);
          }
        }, 100);
      }
    }
  }

  // 用素材数据填充附件
  function fillAttachmentWithMaterial(material, attachmentType, targetAttachmentId = null) {
    console.log(`⚠️ [WARNING] 直接调用fillAttachmentWithMaterial，可能跳过了跨场景转换！`, {
      stepType,
      materialId: material.id,
      attachmentType,
      targetAttachmentId
    });
    // 使用ref获取最新的attachments状态，解决闭包陷阱问题
    const currentAttachments = attachmentsRef.current;
    
    console.log('fillAttachmentWithMaterial开始执行:', {
      materialType: material.materialType || material.msgtype,
      attachmentType,
      targetAttachmentId,
      attachmentsCount: currentAttachments.length,
      attachmentIds: currentAttachments.map(att => att.id)
    });
    
    let targetAttachment;
    
    if (targetAttachmentId) {
      // 如果指定了目标附件ID，直接查找
      targetAttachment = currentAttachments.find(att => att.id === targetAttachmentId);
      console.log('通过ID查找目标附件:', {
        targetAttachmentId,
        found: !!targetAttachment,
        targetAttachment: targetAttachment
      });
    } else {
      // 找到最新创建的附件（通常是列表中的最后一个同类型附件）
      const sameTypeAttachments = currentAttachments.filter(att => att.type === attachmentType);
      targetAttachment = sameTypeAttachments[sameTypeAttachments.length - 1];
      console.log('通过类型查找最新附件:', {
        attachmentType,
        sameTypeCount: sameTypeAttachments.length,
        found: !!targetAttachment,
        targetAttachment: targetAttachment
      });
    }
    
    if (!targetAttachment) {
      console.warn('未找到目标附件');
      return;
    }
    
    try {
      // 处理企业微信格式数据，统一字段名称
      const materialType = material.materialType || material.msgtype;
      
      switch (materialType) {
        case 'IMAGE':
        case 'image':
          // 处理图片数据 - 兼容两种数据结构
          const imageData = material.imageInfo || material.image;
          const imageMediaId = imageData?.mediaId || imageData?.media_id;
          const imageUrl = imageData?.url || imageData?.pic_url;
          
          // 对于图片，只要有imageData和imageUrl就可以处理，mediaId可以为空
          if (imageData && imageUrl) {
            // 如果没有mediaId，生成一个临时的唯一标识符
            // 检查是否有有效的mediaId
            if (!imageMediaId || imageMediaId.startsWith('temp_')) {
              const materialName = material.name || '未命名素材';
              message.warning(`素材"${materialName}"尚未上传完毕，请稍后刷新重试`);
              return; // 直接返回，不添加这个素材
            }
            
            const effectiveMediaId = imageMediaId;
            
            // 检查当前图片组中的附件数量 - 修正ossUrls的初始格式为对象
            // 重新从当前attachments中获取最新的targetAttachment状态
            const latestAttachments = attachmentsRef.current;
            const latestTargetAttachment = latestAttachments.find(att => att.id === targetAttachment.id);
            const currentImageGroup = latestTargetAttachment?.imageGroup || { mediaIds: [], ossUrls: {} };
            const currentCount = currentImageGroup.mediaIds.length;
            
            console.log('当前图片组状态:', {
              attachmentId: targetAttachment.id,
              currentCount,
              mediaIds: currentImageGroup.mediaIds,
              effectiveMediaId
            });
            
            // 计算添加后的总附件数
            const otherAttachmentsCount = latestAttachments.reduce((count, att) => {
              if (att.id === targetAttachment.id) return count; // 跳过当前图片组
              if (att.type === 'TEXT' || att.type === 'MOMENT_TEXT') return count; // 跳过文字类型
              
              if (['IMAGE_GROUP', 'MOMENT_IMAGE_GROUP'].includes(att.type)) {
                return count + (att.imageGroup ? att.imageGroup.mediaIds.length : 1);
              } else {
                return count + 1;
              }
            }, 0);
            
            const totalAfterAdd = otherAttachmentsCount + currentCount + 1;
            
            if (totalAfterAdd > 9) {
              Modal.warning({
                title: '附件数量限制',
                content: '最多只能添加9个附件，添加此图片将超出限制'
              });
              return;
            }
            
            // 添加图片到现有图片组 - 修正ossUrls格式为对象，键为mediaId
            const newImageGroup = {
              mediaIds: [...currentImageGroup.mediaIds, effectiveMediaId],
              ossUrls: {
                ...currentImageGroup.ossUrls,
                [effectiveMediaId]: imageUrl || ''
              }
            };
            
            onUpdateContent(targetAttachment.id, 'imageGroup', newImageGroup);
            
            // 直接更新全局预览URLs，确保MediaUpload组件能收到更新
            if (imageUrl && effectiveMediaId && typeof handleUpdatePreviewUrls === 'function') {
              handleUpdatePreviewUrls({ [effectiveMediaId]: imageUrl });
            }
          } else {
            console.warn('图片素材数据不完整:', { 
              imageData: !!imageData, 
              imageUrl: !!imageUrl, 
              imageMediaId, 
              material: material?.name || '未知素材' 
            });
            Modal.warning({
              title: '素材数据不完整',
              content: '选择的图片素材缺少必要的URL信息，无法添加到附件中'
            });
          }
          break;
          
        case 'VIDEO':
        case 'video':
          // 处理视频数据 - 兼容两种数据结构
          const videoData = material.videoInfo || material.video;
          const videoMediaId = videoData?.mediaId || videoData?.media_id;
          const videoUrl = videoData?.url;
          const videoTitle = videoData?.title || material.name;
          const videoDescription = videoData?.description || material.description;
          
          // 对于视频，只要有videoData和videoUrl就可以处理，mediaId可以为空
          if (videoData && videoUrl) {
            // 如果没有mediaId，生成一个临时的唯一标识符
            // 检查是否有有效的videoMediaId
            if (!videoMediaId || videoMediaId.startsWith('temp_')) {
              const materialName = material.name || '未命名素材';
              message.warning(`视频素材"${materialName}"尚未上传完毕，请稍后刷新重试`);
              return; // 直接返回，不添加这个素材
            }
            
            const effectiveVideoMediaId = videoMediaId;
            
            console.log('准备更新视频附件数据:', {
              targetAttachmentId: targetAttachment.id,
              effectiveVideoMediaId,
              videoUrl,
              videoTitle,
              videoDescription
            });
            
            onUpdateContent(targetAttachment.id, 'mediaId', effectiveVideoMediaId);
            onUpdateContent(targetAttachment.id, 'ossUrl', videoUrl || '');
            onUpdateContent(targetAttachment.id, 'videoTitle', videoTitle || '');
            onUpdateContent(targetAttachment.id, 'videoDescription', videoDescription || '');
            
            // 直接更新全局预览URLs，确保MediaUpload组件能收到更新
            if (videoUrl && effectiveVideoMediaId && typeof handleUpdatePreviewUrls === 'function') {
              handleUpdatePreviewUrls({ [effectiveVideoMediaId]: videoUrl });
            }
            
            // 同时更新预览URLs - 使用模拟的方式来触发预览URL更新
            if (videoUrl && handleFileUpload) {
              try {
                // 创建一个模拟的上传成功结果来触发预览URL更新
                const mockUploadResult = {
                  mediaId: videoMediaId,
                  ossUrl: videoUrl,
                  success: true
                };
                
                const mockOptions = {
                  file: new File([], 'material-video', { type: 'video/mp4' }),
                  onSuccess: (result) => {
                    console.log('素材视频预览URL更新成功:', result);
                  },
                  onError: () => {},
                  onProgress: () => {},
                  fieldKey: targetAttachment.id,
                  mediaType: 'video',
                  stepType
                };
                
                // 延迟触发成功回调，确保状态已更新
                setTimeout(() => {
                  if (mockOptions.onSuccess) {
                    mockOptions.onSuccess(mockUploadResult);
                  }
                }, 100);
              } catch (error) {
                console.warn('更新视频预览URL失败:', error);
              }
            }
          } else {
            console.warn('视频素材数据不完整:', { 
              videoData: !!videoData, 
              videoUrl: !!videoUrl, 
              videoMediaId, 
              material: material?.name || '未知素材' 
            });
            Modal.warning({
              title: '素材数据不完整',
              content: '选择的视频素材缺少必要的URL信息，无法添加到附件中'
            });
          }
          break;
          
        case 'LINK':
        case 'link':
        case 'news': // 企业微信中可能使用news类型
          // 处理链接数据 - 兼容两种数据结构
          const linkData = material.linkInfo || material.news || material.link;
          const linkTitle = linkData?.title || material.name;
          const linkDescription = linkData?.description || material.description;
          const linkUrl = linkData?.url;
          const linkPicUrl = linkData?.picUrl || linkData?.pic_url;
          
          if (linkData) {
            onUpdateContent(targetAttachment.id, 'title', linkTitle || '');
            onUpdateContent(targetAttachment.id, 'description', linkDescription || '');
            onUpdateContent(targetAttachment.id, 'url', linkUrl || '');
            if (linkPicUrl) {
              onUpdateContent(targetAttachment.id, 'picUrl', linkPicUrl);
            }
          }
          break;
          
        case 'MINIPROGRAM':
        case 'miniprogram':
          // 处理小程序数据 - 兼容多种数据结构
          const miniprogramData = material.miniprogramInfo || material.miniprogram;
          
          console.log('🔍 小程序素材数据调试:', {
            material: material,
            miniprogramData: miniprogramData,
            materialName: material.name,
            allKeys: miniprogramData ? Object.keys(miniprogramData) : '无数据'
          });
          
          const miniprogramTitle = miniprogramData?.title || material.name;
          const miniprogramAppid = miniprogramData?.appid;
          // 支持多种页面路径字段名
          const miniprogramPagepath = miniprogramData?.pagepath || miniprogramData?.page_path || miniprogramData?.page || miniprogramData?.pageParam;
          // 支持多种缩略图字段名 - 优先使用后端返回的标准字段名
          const miniprogramThumbMediaId = miniprogramData?.picMediaId || miniprogramData?.thumbMediaId || miniprogramData?.thumb_media_id || miniprogramData?.pic_media_id || miniprogramData?.mediaId;
          const miniprogramThumbUrl = miniprogramData?.picUrl || miniprogramData?.thumbUrl || miniprogramData?.thumb_url || miniprogramData?.pic_url || miniprogramData?.url;
          
          console.log('🖼️ 小程序缩略图信息详细调试:', {
            miniprogramThumbMediaId,
            miniprogramThumbUrl,
            picMediaId: miniprogramData?.picMediaId,
            picUrl: miniprogramData?.picUrl,
            thumbMediaId: miniprogramData?.thumbMediaId,
            thumb_media_id: miniprogramData?.thumb_media_id,
            pic_media_id: miniprogramData?.pic_media_id,
            mediaId: miniprogramData?.mediaId,
            thumbUrl: miniprogramData?.thumbUrl,
            thumb_url: miniprogramData?.thumb_url,
            pic_url: miniprogramData?.pic_url,
            url: miniprogramData?.url,
            targetAttachmentId: targetAttachment.id,
            miniprogramDataStructure: miniprogramData
          });

          if (miniprogramData) {
            onUpdateContent(targetAttachment.id, 'title', miniprogramTitle || '');
            onUpdateContent(targetAttachment.id, 'appid', miniprogramAppid || '');
            onUpdateContent(targetAttachment.id, 'pagepath', miniprogramPagepath || '');
            
            // 处理缩略图 - 优先使用thumbUrl，如果没有则用thumbMediaId
            if (miniprogramThumbUrl && miniprogramThumbUrl.trim() !== '') {
              // 如果有缩略图URL，生成一个临时mediaId（如果原始mediaId为空）
              // 对于小程序缩略图，如果有thumbUrl但没有有效的thumbMediaId，给出提示
              if (miniprogramThumbMediaId && miniprogramThumbMediaId.startsWith('temp_')) {
                const materialName = material.name || '未命名素材';
                message.warning(`小程序素材"${materialName}"的缩略图尚未上传完毕，请稍后刷新重试`);
                return; // 直接返回，不添加这个素材
              }
              
              const effectiveThumbMediaId = miniprogramThumbMediaId;
              
              console.log('✅ 小程序素材有缩略图URL，直接使用:', {
                effectiveThumbMediaId,
                miniprogramThumbUrl,
                targetAttachmentId: targetAttachment.id
              });
              
              onUpdateContent(targetAttachment.id, 'thumbMediaId', effectiveThumbMediaId);
              onUpdateContent(targetAttachment.id, 'thumbMediaId_ossUrl', miniprogramThumbUrl);
              
              // 直接更新全局预览URLs，确保MediaUpload组件能收到更新
              if (typeof handleUpdatePreviewUrls === 'function') {
                console.log('🔄 更新小程序缩略图全局预览URLs:', { [effectiveThumbMediaId]: miniprogramThumbUrl });
                handleUpdatePreviewUrls({ [effectiveThumbMediaId]: miniprogramThumbUrl });
              }
            } else if (miniprogramThumbMediaId && miniprogramThumbMediaId.trim() !== '') {
              // 只有mediaId没有URL的情况 - 通过后端获取对应的OSS URL
              console.log('🔍 小程序素材只有mediaId，需要通过API获取URL:', miniprogramThumbMediaId);
              
              onUpdateContent(targetAttachment.id, 'thumbMediaId', miniprogramThumbMediaId);
              
              // 调用后端API获取mediaId对应的OSS URL
              const fetchMediaInfo = async () => {
                try {
                  const tenantId = getTenantId();
                  if (!tenantId) {
                    console.warn('无法获取租户ID，跳过媒体信息获取');
                    return;
                  }
                  
                  console.log('📡 正在调用API获取小程序缩略图URL:', {
                    mediaId: miniprogramThumbMediaId,
                    tenantId
                  });
                  
                  const response = await api.get(`/media/wework/by_media_id?mediaId=${encodeURIComponent(miniprogramThumbMediaId)}&tenantId=${tenantId}`);
                  
                  if (response.data.success && response.data.ossUrl) {
                    const ossUrl = response.data.ossUrl;
                    console.log('✅ 成功获取小程序缩略图OSS URL:', ossUrl);
                    
                    // 更新附件的OSS URL
                    onUpdateContent(targetAttachment.id, 'thumbMediaId_ossUrl', ossUrl);
                    
                    // 更新全局预览URLs
                    if (typeof handleUpdatePreviewUrls === 'function') {
                      console.log('🔄 通过API更新小程序缩略图全局预览URLs:', { [miniprogramThumbMediaId]: ossUrl });
                      handleUpdatePreviewUrls({ [miniprogramThumbMediaId]: ossUrl });
                    }
                  } else {
                    console.warn('❌ 获取小程序缩略图媒体信息失败:', response.data.message);
                  }
                } catch (error) {
                  console.error('❌ 获取小程序缩略图媒体信息异常:', error);
                }
              };
              
              // 延迟执行，确保组件状态已更新
              setTimeout(fetchMediaInfo, 100);
            } else {
              console.warn('⚠️ 小程序素材没有任何缩略图信息:', { 
                miniprogramThumbUrl, 
                miniprogramThumbMediaId,
                materialName: material.name,
                miniprogramData 
              });
            }
          } else {
            console.warn('小程序素材数据不完整:', material);
          }
          break;
          
        default:
          console.warn('不支持的素材类型:', materialType, '原始数据:', material);
      }
    } catch (error) {
      console.error('填充附件数据失败:', error);
    }
  }
};

export default AttachmentManager; 